import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const ReviewModal = ({ isOpen, onClose, targetType, targetId, currentUserId, onEditClick }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `/api/reviews/target/${targetType}/${targetId}`
      );
      setReviews(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load reviews');
      console.error('Fetch reviews error:', err);
    } finally {
      setLoading(false);
    }
  }, [targetType, targetId]);

  useEffect(() => {
    if (isOpen && targetType && targetId) {
      fetchReviews();
    }
  }, [isOpen, targetType, targetId, fetchReviews]);

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    setDeletingId(reviewId);

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `/api/reviews/${reviewId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Remove review from list
      setReviews(reviews.filter(r => r._id !== reviewId));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete review');
    } finally {
      setDeletingId(null);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="star-rating-display">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`star ${star <= rating ? 'filled' : 'empty'}`}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="review-modal-backdrop" onClick={handleBackdropClick}>
      <div className="review-modal-content">
        <div className="review-modal-header">
          <h2>Reviews ({reviews.length})</h2>
          <button className="modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="review-modal-body">
          {loading ? (
            <div className="reviews-loading">
              <div className="spinner"></div>
              <p>Loading reviews...</p>
            </div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : reviews.length === 0 ? (
            <div className="no-reviews">
              <p>No reviews yet. Be the first to review!</p>
            </div>
          ) : (
            <div className="reviews-scrollable-list">
              {reviews.map((review) => (
                <div key={review._id} className="review-modal-item">
                   <div className="review-header">
                     <div className="review-user-info">
                       <div className="user-avatar">
                         {review.user?.profile?.avatar?.url ? (  // FIXED: Changed from review.user?.avatar
                           <img
                             src={review.user.profile.avatar.url}  // FIXED: Changed from /api/media/${review.user.avatar}
                               alt={review.user.username}
                               onError={(e) => {
                               e.target.style.display = 'none';
                               e.target.nextSibling.style.display = 'flex';
                             }}
                           />
                         ) : null}
                         <div className="avatar-placeholder" style={{ display: review.user?.profile?.avatar?.url ? 'none' : 'flex' }}>  // FIXED
                           {review.user?.username?.[0]?.toUpperCase() || '?'}
                         </div>
                       </div>
                      <div className="user-details">
                        <span className="username">{review.user?.username || 'Anonymous'}</span>
                        <span className="review-date">{formatDate(review.createdAt)}</span>
                      </div>
                    </div>

                    <div className="review-rating">
                      {renderStars(review.rating)}
                      <span className="rating-number">{review.rating}/5</span>
                    </div>
                  </div>

                  <div className="review-content">
                    <p>{review.comment}</p>
                  </div>

                  {currentUserId && review.user?._id === currentUserId && (
                    <div className="review-actions">
                      <button
                        className="btn btn-small btn-secondary"
                        onClick={() => {
                          onEditClick(review);
                          onClose();
                        }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => handleDelete(review._id)}
                        disabled={deletingId === review._id}
                      >
                        {deletingId === review._id ? 'Deleting...' : '🗑️ Delete'}
                      </button>
                    </div>
                  )}

                  {review.status === 'pending' && review.user?._id === currentUserId && (
                    <div className="review-status-badge pending">
                      ⏳ Pending approval
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;
